import { Edit, OpenInNew } from '@mui/icons-material';
import { Box, Chip, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { carFieldsMapping } from '../../../utils/constants/carData';
import { mileageUnits } from '../../../utils/constants/myGarage';
import { DrivingCondition, DrivingConditionReverseMap } from '../../../utils/constants/sellMyCar';
import { getCommaFormattedNumber } from '../../../utils/methods/stringMethods';
import { reducerPaths } from '../../../app/reducerPaths';

const organizeVehicleData = (vehicleData, t) => {
  // Section 1: Vehicle Details (Basic identity and appearance info)
  const vehicleDetails = [
    { label: t('VIN'), value: vehicleData?.VIN },
    { label: t('Make'), value: vehicleData?.Make },
    { label: t('Model'), value: vehicleData?.Model },
    { label: t('Year'), value: vehicleData?.Year },
    { label: t('Trim'), value: vehicleData?.Trim },
    { label: t('Color'), value: vehicleData?.color },
  ];

  // Section 2: Specifications & Equipment (Usage, configuration, and included items)
  const getTiresValue = () => {
    if (Array.isArray(vehicleData?.tires) && vehicleData.tires.length > 0) {
      return vehicleData.tires.join(', ');
    }
    return t('N/A');
  };

  const specifications = [
    { label: t('Odometer'), value: vehicleData?.Mileage },
    { label: t('Transmission'), value: vehicleData?.transmission },
    { label: t('Keys'), value: vehicleData?.key },
    { label: t('Tires'), value: getTiresValue() },
    { label: t('Location'), value: vehicleData?.Location },
  ];

  // Section 3: Condition & Reports (Vehicle state and documentation)
  const getInteriorDamage = () => {
    if (Array.isArray(vehicleData?.interiorDamage) && vehicleData.interiorDamage.length > 0) {
      return vehicleData.interiorDamage.join(', ');
    }
    return t('No Damage');
  };

  const getExteriorDamage = () => {
    if (Array.isArray(vehicleData?.exteriorDamage) && vehicleData.exteriorDamage.length > 0) {
      const uniqueDamages = [...new Set(vehicleData.exteriorDamage)];
      return uniqueDamages.join(', ');
    }
    return t('No Damage');
  };

  const conditionReports = [
    {
      label: t('Condition'),
      value: vehicleData?.check_driving_condition,
    },
    { label: t('Interior Damage'), value: getInteriorDamage() },
    { label: t('Exterior Damage'), value: getExteriorDamage() },
    { label: t('Carfax Report'), value: vehicleData?.Carfax_Report_Url },
  ];

  return [vehicleDetails, specifications, conditionReports];
};

const VehicleInformation = ({ vehicleData = {}, onClickEdit, showEdit }) => {
  const { t } = useTranslation('carDetails');
  
  const dispatch = useDispatch();
  const auth = useSelector(store => store[reducerPaths.auth])

  const theme = useTheme();

  const sections = organizeVehicleData(vehicleData, t);

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const getDrivingConditionBadge = () => {
    if (vehicleData?.check_driving_condition === 'sold_as_is') {
      return (
        <Chip
          label={t("Sold AS IS")}
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            backgroundColor: '#d32f2f',
            color: '#fff',
            '& .MuiChip-icon': {
              color: '#fff',
            },
            boxShadow: '0 2px 4px rgba(211, 47, 47, 0.3)',
          }}
        />
      );
    }
    if (vehicleData?.check_driving_condition === 'run_and_drive') {
      return (
        <Chip
          label={t("Run and Drive")}
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            backgroundColor: '#2e7d32',
            color: '#fff',
            '& .MuiChip-icon': {
              color: '#fff',
            },
            boxShadow: '0 2px 4px rgba(46, 125, 50, 0.3)',
          }}
        />
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#fafafa',
        borderRadius: '9px',
        padding: '1rem',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e0e0e0'}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h5" fontWeight={700} color={theme.palette.primary.main}>
            {t('Vehicle Information')}
          </Typography>

          {getDrivingConditionBadge()}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          {vehicleData?.Relist_Count > 0 && (
            <Chip
              label={t('Relist Count: {{count}}', { count: vehicleData?.Relist_Count })}
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                boxShadow: `0 2px 4px ${theme.palette.primary.main}40`,
              }}
            />
          )}
          {showEdit && auth?.user_id === parseInt(vehicleData?.Seller_Id) && (
            <Tooltip title={t("Edit Vehicle Information")} arrow>
              <IconButton
                onClick={onClickEdit}
                sx={{
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#fff',
                  border: `1px solid ${theme.palette.primary.main}`,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.main,
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '1.5rem',
            flexWrap: 'nowrap',
            minWidth: 'fit-content',
          }}
        >
          {sections?.map((section, idx) => (
            <Box
              key={idx}
              sx={{
                minWidth: '200px',
                flex: '1 1 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#fff',
                padding: '1.25rem',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e8e8e8'}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow:
                    theme.palette.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {section.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.25rem',
                    paddingBottom: '0.50rem',
                    borderBottom:
                      i < section.length - 1
                        ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f0f0'}`
                        : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: `${16 / 16}rem`,
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      letterSpacing: '0.5px',
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: `${16 / 16}rem`,
                      fontWeight: 400,
                      color: theme.palette.mode === 'dark' ? '#b0b0b0' : '#555',
                      wordBreak: 'break-word',
                      lineHeight: 1.5,
                    }}
                  >
                    {item?.label === t('Odometer') ? (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'baseline',
                          gap: '0.25rem',
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '0.938rem',
                            fontWeight: 600,
                            color: theme.palette.mode === 'dark' ? '#b0b0b0' : '#555',
                          }}
                        >
                          {getCommaFormattedNumber(item?.value)}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '0.813rem',
                            fontWeight: 500,
                            color: theme.palette.mode === 'dark' ? '#808080' : '#888',
                          }}
                        >
                          {vehicleData?.Mileage_Unit || mileageUnits.km}
                        </Typography>
                      </Box>
                    ) : item?.label === t('Carfax Report') && item?.value && isValidUrl(item?.value) ? (
                      <Link
                        href={item?.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          fontWeight: 600,
                          fontSize: '0.938rem',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: theme.palette.primary.dark,
                          },
                        }}
                      >
                        {t('View Report')}
                        <OpenInNew sx={{ fontSize: '1rem' }} />
                      </Link>
                    ) : item?.label === t('Condition') && item?.value === 'run_and_drive' ? (
                      t('Run and Drive')
                    ) : item?.label === t('Condition') && item?.value === 'sold_as_is' ? (
                      t('Sold AS IS')
                    ) : item?.value ? (
                      item.value
                    ) : (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.938rem',
                          fontStyle: 'italic',
                          color: theme.palette.mode === 'dark' ? '#666' : '#999',
                        }}
                      >
                        {t('N/A')}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Notes Section */}
      {vehicleData?.Notes && (
        <Box
          sx={{
            marginTop: '1.5rem',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#fff',
            padding: '1.25rem',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e8e8e8'}`,
          }}
        >
          <Typography
            sx={{
              fontSize: `${16 / 16}rem`,
              fontWeight: 600,
              color: theme.palette.primary.main,
              letterSpacing: '0.5px',
              marginBottom: '0.75rem',
            }}
          >
            {t('Notes')}
          </Typography>
          <Typography
            sx={{
              fontSize: `${16 / 16}rem`,
              fontWeight: 400,
              color: theme.palette.mode === 'dark' ? '#b0b0b0' : '#555',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {vehicleData.Notes}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VehicleInformation;